import { Route, Redirect } from 'react-router'
import { Translate } from 'react-redux-i18n'
import React, { useCallback, useEffect, useState } from 'react'
import { toggleModal } from '../actions/modal'
import { ensureValidSession, signOut } from '../actions/users'
import GlobalLoadingIndicator from './global-loading-indicator'

export const scopes = {
  LOGGED_IN: 0,
  ADMIN: 1
}

const PrivateRoute = ({ component: Component, store, scope, ...rest }) => {
  const [stateSnapshot, setStateSnapshot] = useState(store.getState())
  const [status, setStatus] = useState('checking')
  const [authMessage, setAuthMessage] = useState('login.login_needed')

  const allow = useCallback(
    usersState => {
      switch (scope) {
        case scopes.LOGGED_IN:
          return usersState.signedIn
        case scopes.ADMIN:
          const userInfo = usersState.userInfo

          return (
            userInfo?.group?.name === 'admin'
          )
        default:
          return false
      }
    },
    [scope]
  )

  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      setStateSnapshot(store.getState())
    })

    return () => {
      unsubscribe()
    }
  }, [store])

  useEffect(() => {
    let mounted = true

    if (!stateSnapshot.users.signedIn) {
      setAuthMessage('login.login_needed')
      setStatus('unauthorized')
      return () => {
        mounted = false
      }
    }

    setStatus('checking')

    store.dispatch(ensureValidSession()).then(({ isValid, reason }) => {
      if (!mounted) {
        return
      }

      if (!isValid) {
        // Network/5xx on session check is not an auth failure — keep the user
        // signed in and let subsequent API calls handle real 401s.
        if (reason === 'session_check_failed') {
          setStatus(
            allow(store.getState().users) ? 'authorized' : 'unauthorized'
          )
          return
        }

        const message =
          reason === 'session_expired'
            ? 'registration.invalid_login'
            : 'login.login_needed'

        setAuthMessage(message)
        setStatus('unauthorized')

        // LoginModal only opens when !signedIn. Clear a stale signed-in flag
        // (e.g. signedIn with missing access_token) before opening the modal.
        if (store.getState().users.signedIn) {
          store.dispatch(
            signOut({
              remote: false,
              redirectToHome: false,
              reload: false
            })
          )
        }
        return
      }

      setStatus(
        allow(store.getState().users) ? 'authorized' : 'unauthorized'
      )
    })

    return () => {
      mounted = false
    }
  }, [allow, scope, stateSnapshot.users.signedIn, store, rest.path])

  useEffect(() => {
    if (status !== 'unauthorized' || scope === scopes.ADMIN) {
      return
    }

    store.dispatch(toggleModal(true, rest.path, authMessage))
  }, [status, scope, store, rest.path, authMessage])

  /**
   * if user is allowed to enter page with given scope, render said component/page
   * if user is not allowed to enter page with scope LOGGED_IN, render login modal and login_needed page.
   * if user is trying to enter admin scope, redirect to "/", like any invalid path, if not allowed
   */
  return (
    <Route
      {...rest}
      render={props => {
        return status === 'checking' ? (
          <GlobalLoadingIndicator center />
        ) : status === 'authorized' ? (
          <Component {...props} />
        ) : scope === scopes.ADMIN ? (
          <Redirect to="/" />
        ) : (
          <div className="login-notice">
            <Translate value={authMessage} />
          </div>
        )
      }}
    />
  )
}

export default PrivateRoute
